"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileText, TrendingUp, BookOpen, Clock, Calendar, ShieldCheck, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const deliveryCards = [{
  id: "research",
  badge: "Primary Data",
  title: "Proprietary research digests",
  description: "Core charts, peer-reviewed findings, and benchmark metrics drawn straight from our quarterly enterprise studies.",
  meta: "2 core findings & 1 raw data sheet",
  icon: "FileText"
}, {
  id: "frameworks",
  badge: "Strategy",
  title: "Executive playbook breakdowns",
  description: "Deconstructed strategic guides translating complex industry transformations into actionable steps for leadership teams.",
  meta: "1 tactical framework per issue",
  icon: "BookOpen"
}, {
  id: "commentary",
  badge: "Analysis",
  title: "Independent market commentary",
  description: "Unsponsored editorial context on emerging B2B trends, regulatory signals, and long-term organizational shifts.",
  meta: "Strictly zero commercial placements",
  icon: "TrendingUp"
}];
const highlights = [{
  icon: "Calendar",
  label: "Schedule",
  value: "Every Thursday at 7:00 AM EST"
}, {
  icon: "Clock",
  label: "Reading time",
  value: "5-minute focused briefing"
}, {
  icon: "ShieldCheck",
  label: "Editorial standard",
  value: "100% peer-reviewed & ad-free"
}];
export default function Deliverables() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const getCardIcon = (iconName: string) => {
    switch (iconName) {
      case "FileText":
        return <FileText className="size-5 text-foreground" />;
      case "BookOpen":
        return <BookOpen className="size-5 text-foreground" />;
      case "TrendingUp":
        return <TrendingUp className="size-5 text-foreground" />;
      default:
        return <FileText className="size-5 text-foreground" />;
    }
  };
  const getHighlightIcon = (iconName: string) => {
    switch (iconName) {
      case "Calendar":
        return <SafeHydrate><Calendar className="size-4 text-muted-foreground" /></SafeHydrate>;
      case "Clock":
        return <Clock className="size-4 text-muted-foreground" />;
      case "ShieldCheck":
        return <ShieldCheck className="size-4 text-muted-foreground" />;
      default:
        return <SafeHydrate><Calendar className="size-4 text-muted-foreground" /></SafeHydrate>;
    }
  };
  return <section id="what-youll-receive" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border transition-colors">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
        {/* Editorial Section Masthead Rule */}
        <div className="flex flex-col gap-1 mb-8 sm:mb-12">
          <div className="h-px w-full bg-foreground" />
          <div className="h-[0.5px] w-full bg-border" />
        </div>

        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-8 border-b border-border">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Briefing structure
            </span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-tight">
              What arrives in your inbox every Thursday
            </h2>
          </div>
          <p className="max-w-md text-base text-muted-foreground leading-relaxed">
            A concise five-minute review of market shifts, proprietary research excerpts, and executive playbooks prepared by our senior editorial board.
          </p>
        </div>

        {/* Meta Bar Strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-6 border-b border-border bg-muted/40 px-4 sm:px-6 rounded-md my-8">
          {highlights.map((item, i) => <div key={item.label} data-index={i} className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-card border border-border">
                {getHighlightIcon(item.icon)}
              </div>
              <div>
                <span className="block text-xs uppercase tracking-wider text-muted-foreground">
                  {item.label}
                </span>
                <span className="text-sm font-medium text-foreground">
                  {item.value}
                </span>
              </div>
            </div>)}
        </div>

        {/* Three-Card Deliverable Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 pt-4">
          {deliveryCards.map((card, i) => <motion.div key={card.id} variants={itemVariants} data-index={i} className="h-full">
              <Card className="h-full flex flex-col justify-between rounded-md border border-border bg-card p-6 shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300 hover:border-foreground/30">
                <div>
                  <div className="flex items-center justify-between gap-4 pb-5 border-b border-border">
                    <span className="px-2.5 py-1 rounded-sm bg-secondary text-secondary-foreground text-xs font-mono uppercase tracking-normal">
                      {card.badge}
                    </span>
                    <div className="p-2 rounded-sm bg-background border border-border">
                      {getCardIcon(card.icon)}
                    </div>
                  </div>

                  <CardHeader className="px-0 pt-6 pb-3">
                    <CardTitle className="font-serif text-xl sm:text-2xl font-semibold text-card-foreground leading-snug">
                      {card.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="px-0 py-0">
                    <CardDescription className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                      {card.description}
                    </CardDescription>
                  </CardContent>
                </div>

                <div className="pt-6 mt-6 border-t border-border flex items-center justify-between text-xs text-muted-foreground font-mono">
                  <span>{card.meta}</span>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Bottom Context Callout & Archive Link */}
        <div className="mt-12 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Never monetized via sponsored links, product affiliates, or third-party endorsements.
          </p>
          <div className="flex items-center gap-3">
            <Button asChild variant="outline" size="sm" className="rounded-sm border-border text-foreground hover:bg-muted">
              <Link to="/ResearchReportsPage">
                <span>Browse research library</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}